import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X, User, Phone, Hash, Loader2, ChevronRight, Eye } from 'lucide-react';
import { formatDateIST } from '../utils/date';
import PatientQuickPeekDrawer from './PatientQuickPeekDrawer';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * GlobalPatientSearch — header-level unified search that resolves a
 * patient by name, phone or MRN and offers a quick-peek drawer +
 * deep-link into the full profile page.
 *
 * Mounted inside any role dashboard (doctor / reception / pharmacy).
 * Triggers `/api/patients/search` after a 220ms debounce, renders an
 * overlay dropdown with keyboard-navigable results.
 */
export default function GlobalPatientSearch({ onPickQuick }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  // Quick-peek drawer state (independent of full-page navigation)
  const [peekId, setPeekId] = useState(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // debounce + abort prior request
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/patients/search`, {
          params: { q, limit: 12 },
          withCredentials: true,
          signal: ctrl.signal,
        });
        setResults(data?.results || []);
        setActive(0);
      } catch (_) { /* aborted / 401 */ }
      setLoading(false);
    }, 220);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  // close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // ⌘K / Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Default behaviour: open the lightweight quick-peek drawer so the
  // user (esp. doctors mid-consult) doesn't lose the current screen.
  // Hosts can override via `onPickQuick`.
  const pick = (r) => {
    setOpen(false);
    setQ('');
    if (onPickQuick) { onPickQuick(r); return; }
    setPeekId(r.patient_id);
  };

  // Skip the peek and go straight to the full profile page.
  const openFull = (r, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setOpen(false);
    setQ('');
    navigate(`/patients/${r.patient_id}`);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <div ref={wrapRef} className="relative w-full max-w-md" data-testid="global-patient-search">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search patient by name, phone, or MRN…"
          className="w-full pl-9 pr-16 py-2 text-sm rounded-lg border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
          data-testid="global-search-input"
          aria-label="Search patient"
        />
        {q ? (
          <button
            onClick={() => { setQ(''); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
            data-testid="global-search-clear"
            aria-label="Clear"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        ) : (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
            ⌘K
          </span>
        )}
      </div>

      {open && (q || loading) && (
        <div
          className="absolute z-40 mt-1 w-[120%] max-w-[480px] -left-4 bg-white border border-gray-200 shadow-lg rounded-xl overflow-hidden"
          data-testid="global-search-dropdown"
        >
          {loading && (
            <div className="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && q && (
            <div className="px-4 py-4 text-xs text-gray-400 text-center" data-testid="global-search-empty">
              No patients match <span className="font-mono text-gray-600">&quot;{q}&quot;</span>
            </div>
          )}
          {!loading && results.length > 0 && (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100" data-testid="global-search-results">
              {results.map((r, i) => (
                <li
                  key={r.patient_id}
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  onMouseEnter={() => setActive(i)}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${
                    active === i ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`global-search-result-${i}`}
                >
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
                    {(r.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-bold text-gray-900 truncate">{r.name || '—'}</span>
                      {r.age && <span className="text-[10px] text-gray-500">{r.age}y</span>}
                      {r.gender && <span className="text-[10px] text-gray-400 capitalize">· {r.gender}</span>}
                      {r.is_primary === false && (
                        <span className="text-[9px] uppercase tracking-wider bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">
                          {r.relationship}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-0.5"><Hash className="h-3 w-3" /> {r.mrn}</span>
                      {r.phone && <span className="inline-flex items-center gap-0.5"><Phone className="h-3 w-3" /> {r.phone}</span>}
                      {typeof r.visits === 'number' && (
                        <span className="text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0 rounded">
                          {r.visits} visit{r.visits === 1 ? '' : 's'}
                        </span>
                      )}
                      {r.last_visit && (
                        <span className="text-gray-400">· last {formatDateIST(r.last_visit)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onMouseDown={(e) => openFull(r, e)}
                    className="text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-1.5 py-1 rounded inline-flex items-center gap-1 flex-shrink-0"
                    title="Open full profile"
                    data-testid={`global-search-open-full-${i}`}
                  >
                    Full <ChevronRight className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 flex items-center justify-between">
            <span><Eye className="h-3 w-3 inline mr-1" /> Click for quick peek · use &quot;Full&quot; to deep-link</span>
            <span>↑↓ navigate · ↵ open · esc close</span>
          </div>
        </div>
      )}

      {/* Quick-Peek Drawer (only when no onPickQuick override) */}
      {!onPickQuick && (
        <PatientQuickPeekDrawer
          open={!!peekId}
          patientId={peekId}
          onClose={() => setPeekId(null)}
        />
      )}
    </div>
  );
}
